import type {
    AccordionContentProps,
    ObjectShorthandValue,
} from '@fluentui/react-northstar';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import {
    useStyles,
    Flex,
    Accordion,
    useAppDisplayModeContext,
    Caption,
} from '@archipro-design/aria';
import {
    AddLine,
    SubtractLine,
    AngleUp,
    AngleDown,
} from '@archipro-design/icons';
import {
    Accordion as RoccoAccordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@rocco/components/accordion';
import { cn } from '@rocco/utils/cn';
import { useCategoryRevamp } from '@modules/category/hook/use-category-revamp';
import type { AppliedFilterItem } from '~/modules/search/type/filter-state';
import * as S from './FilterAccordion.style';
import { getInitialActivePanelsKeys } from '@modules/search/util/get-initial-active-panel-keys';
import { useTracker } from '@archipro-website/tracker';

export interface AccordionPanel {
    content: ReactNode;
    title: {
        key: string;
        title: string;
        applied: boolean;
        appliedFilters: AppliedFilterItem[];
    };
    className?: string;
    activePanel?: boolean;
}

interface FilterAccordionProps {
    panels: AccordionPanel[];
}

const getAppliedFilterString = (appliedFilters: AppliedFilterItem[]) => {
    const appliedFiltersCount = appliedFilters.length;
    const appliedFiltersTitle = appliedFilters
        .slice(0, 10)
        .map((appliedFilter) => appliedFilter.label)
        .join(', ');
    return appliedFiltersCount > 0
        ? `(${appliedFiltersCount}) ${appliedFiltersTitle}`
        : '';
};

const getActivePanel = (arr: AccordionPanel[]): string[] => {
    const activePanelKeys: string[] = [];
    arr.forEach((obj) => {
        if (obj.activePanel) {
            activePanelKeys.push(obj.title.key);
        }
    });
    return activePanelKeys;
};

/**
 * Open-section state shared by both accordion implementations: seeds from the
 * panels flagged active, re-seeds when SPA navigation swaps the panel set, and
 * logs a `FilterOpen` event for each section the user opens.
 */
const useActivePanels = (panels: AccordionPanel[]) => {
    const { mobile } = useAppDisplayModeContext();
    const defaultActivePanels = useRef<string[]>(getActivePanel(panels));
    const tracker = useTracker();

    const [activePanels, setActivePanels] = useState<string[]>(
        defaultActivePanels.current,
    );

    useEffect(() => {
        // SideEffect: Need to update Accordion open state when SPA navigation updates panel state
        // we should only update this when the default active panel changes
        const refreshPanels = mobile
            ? getActivePanel(panels)
            : getInitialActivePanelsKeys(panels);
        const originalPanels = defaultActivePanels.current;
        if (
            originalPanels.length !== refreshPanels.length ||
            refreshPanels.find((paned) => !originalPanels.includes(paned))
        ) {
            defaultActivePanels.current = refreshPanels;
            setActivePanels(refreshPanels);
        }
    }, [panels, mobile]);

    const changeActivePanels = (newActivePanels: string[]) => {
        const newlyOpenedPanels = newActivePanels.filter(
            (panelKey) => !activePanels.includes(panelKey),
        );

        newlyOpenedPanels.forEach((panelKey) => {
            const panel = panels.find((p) => p.title.key === panelKey);
            if (panel) {
                tracker.log('FilterOpen', {
                    url: new URL(window.location.href),
                    data: {
                        ExtraData: panel.title.title,
                    },
                });
            }
        });

        setActivePanels(newActivePanels);
    };

    return { activePanels, changeActivePanels };
};

/**
 * Filter-section accordion built on the FluentUI (Aria) `Accordion`. This is
 * what the legacy directory, rooms, professional-tab and search filters render
 * unless the category-pages revamp is active.
 */
const FilterAccordionAria = ({ panels }: FilterAccordionProps) => {
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();
    const { activePanels, changeActivePanels } = useActivePanels(panels);

    return (
        <Accordion
            className={`${css(S.AccordionBase)} accordion-filters`}
            activeIndex={panels.reduce((indexes, panel, index) => {
                if (activePanels.includes(panel.title.key)) indexes.push(index);
                return indexes;
            }, [] as number[])}
            onActiveIndexChange={(_, data) => {
                const activeIndex = data?.activeIndex;
                if (activeIndex === undefined) return;
                const indexes = Array.isArray(activeIndex)
                    ? activeIndex
                    : [activeIndex];
                changeActivePanels(
                    panels
                        .filter((_, index) => indexes.includes(index))
                        .map((panel) => panel.title.key),
                );
            }}
            panels={panels.map((panel) => {
                const { title: titleProps, content } = panel;
                const { title, appliedFilters, key } = titleProps;
                const accordionId = `${key ?? title}-accordian`;
                return {
                    key: key ?? title,
                    title: {
                        id: accordionId,
                        accordionContentId: accordionId,
                        key: key ?? title,
                        content: (
                            <AriaAccordionTitle
                                title={title}
                                appliedFilters={appliedFilters}
                            />
                        ),
                    },
                    // FluentUI's shorthand clones the panel element and merges the
                    // content props onto it, so `content` is spread rather than
                    // nested — nesting it would add a wrapper element.
                    content: {
                        accordionTitleId: accordionId,
                        ...(content as ObjectShorthandValue<AccordionContentProps>),
                    },
                };
            })}
            indicatorIcon={mobile ? <AddLine /> : <AngleDown />}
            indicatorIconActive={mobile ? <SubtractLine /> : <AngleUp />}
        />
    );
};

const AriaAccordionTitle = ({
    title,
    appliedFilters = [],
}: {
    title: string;
    appliedFilters: AppliedFilterItem[];
}) => {
    const { desktop } = useAppDisplayModeContext();
    const { css } = useStyles();
    return (
        <Flex vAlign="center" className={css(S.AccordionTitle)}>
            <div className="title">
                {title === 'Blogposts category' ? 'Articles category' : title}
            </div>
            {!desktop && (
                <Caption variant="04" weight="regular" className="title-note">
                    {getAppliedFilterString(appliedFilters)}
                </Caption>
            )}
        </Flex>
    );
};

/**
 * Filter-section accordion built on the Rocco (Radix) accordion. Multiple
 * sections can be open at once (`type="multiple"`), the open set is controlled
 * so SPA navigation can refresh it. Mobile uses a plus/minus indicator; desktop
 * a rotating chevron.
 *
 * The root carries `accordion-filters-rocco` rather than the FluentUI path's
 * `accordion-filters` so the two implementations' style rules cannot match each
 * other's DOM.
 */
const FilterAccordionRocco = ({ panels }: FilterAccordionProps) => {
    const { mobile } = useAppDisplayModeContext();
    const { activePanels, changeActivePanels } = useActivePanels(panels);

    return (
        <RoccoAccordion
            type="multiple"
            value={activePanels}
            onValueChange={changeActivePanels}
            className="accordion-filters-rocco"
        >
            {panels.map((panel) => {
                const { title, appliedFilters, key } = panel.title;
                const panelKey = key ?? title;
                return (
                    <AccordionItem
                        key={panelKey}
                        value={panelKey}
                        variant="lightBorder"
                        className={cn('last:border-b-0', panel.className)}
                    >
                        <AccordionTrigger
                            variant="narrow"
                            icon={mobile ? 'plus' : 'chevronDown'}
                            openIcon={mobile ? 'minus' : undefined}
                            isOpen={activePanels.includes(panelKey)}
                            className="gap-3 text-left"
                        >
                            <RoccoFilterSectionTitle
                                title={title}
                                appliedFilters={appliedFilters}
                            />
                        </AccordionTrigger>
                        <AccordionContent>{panel.content}</AccordionContent>
                    </AccordionItem>
                );
            })}
        </RoccoAccordion>
    );
};

const RoccoFilterSectionTitle = ({
    title,
    appliedFilters = [],
}: {
    title: string;
    appliedFilters: AppliedFilterItem[];
}) => {
    const { desktop } = useAppDisplayModeContext();
    return (
        <span className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
            <span className="title shrink-0 text-[1.125rem] leading-tight font-normal">
                {title === 'Blogposts category' ? 'Articles category' : title}
            </span>
            {!desktop && (
                <span className="title-note truncate text-sm text-[#a3a3a3]">
                    {getAppliedFilterString(appliedFilters)}
                </span>
            )}
        </span>
    );
};

/**
 * Picks the filter accordion implementation for this request. The Rocco
 * accordion ships with the 2026 category-pages revamp; every other request
 * keeps the FluentUI accordion the legacy filter surfaces render today.
 */
const FilterAccordion = ({ panels }: FilterAccordionProps) => {
    const categoryRevamp = useCategoryRevamp();

    return categoryRevamp ? (
        <FilterAccordionRocco panels={panels} />
    ) : (
        <FilterAccordionAria panels={panels} />
    );
};

export default FilterAccordion;
